import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Globe, Search, ArrowUpRight, ArrowDownRight, Minus, Settings } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RiskScoreLegend } from '@/components/RiskScoreLegend';
import api from '@/lib/api';
import { getRiskBand, getRiskInterpretation } from '@/lib/utils';
import { NavBar } from '@/components/NavBar';

const RISK_THEMES = {
  geopolitical: 'Geopolitical tensions elevated',
  trade_policy: 'Trade policy tension escalating',
  freight_logistics: 'Freight disruption risk',
  regulatory: 'Regulatory changes pending',
  labour_market: 'Labour market pressure',
  climate_disaster: 'Natural disaster risk active',
  currency_financial: 'Currency volatility elevated',
  supplier_concentration: 'Supplier concentration risk',
};

const CountriesPage = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('risk_desc');
  const [riskFilter, setRiskFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.getCountries();
        setCountries(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const processed = useMemo(() => {
    let result = countries.map(c => {
      const band = getRiskBand(c.overall_risk_score);
      // Determine dominant risk theme from score
      const themes = ['trade_policy', 'geopolitical', 'freight_logistics', 'regulatory', 'climate_disaster', 'currency_financial'];
      const theme = themes[Math.floor((c.overall_risk_score * 7 + c.country_code.charCodeAt(0)) % themes.length)];
      return { ...c, band, dominantTheme: RISK_THEMES[theme] || 'Stable - no major signals' };
    });

    if (search) {
      const s = search.toLowerCase();
      result = result.filter(c => c.country_name.toLowerCase().includes(s) || c.country_code.toLowerCase().includes(s));
    }

    if (riskFilter !== 'all') {
      result = result.filter(c => c.band.level.toLowerCase() === riskFilter);
    }

    if (regionFilter !== 'all') {
      result = result.filter(c => (c.region || 'apac') === regionFilter);
    }

    if (sortBy === 'risk_desc') result.sort((a, b) => b.overall_risk_score - a.overall_risk_score);
    else if (sortBy === 'risk_asc') result.sort((a, b) => a.overall_risk_score - b.overall_risk_score);
    else if (sortBy === 'alpha') result.sort((a, b) => a.country_name.localeCompare(b.country_name));
    else if (sortBy === 'movers') {
      const trendVal = (t) => t === 'up' ? 2 : t === 'down' ? 1 : 0;
      result.sort((a, b) => trendVal(b.risk_trend) - trendVal(a.risk_trend));
    }

    return result;
  }, [countries, search, sortBy, riskFilter, regionFilter]);

  const TrendArrow = ({ trend }) => {
    if (trend === 'up') return <ArrowUpRight className="h-4 w-4 text-[#FF3B30]" />;
    if (trend === 'down') return <ArrowDownRight className="h-4 w-4 text-[#2E7D32]" />;
    return <Minus className="h-4 w-4 text-[#4E6974]" />;
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <NavBar />

      <main className="pt-24 px-4 lg:px-6 pb-8">
        <div className="max-w-[1400px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 mb-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-[#0D3B4F]" data-testid="countries-title">Global country risk</h1>
                <p className="text-[#4E6974] mt-1">{countries.length} countries monitored globally</p>
              </div>
              <RiskScoreLegend compact />
            </div>
          </motion.div>

          {/* Filters */}
          <Card className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4E6974]" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search countries..." className="pl-9" data-testid="countries-search" />
                </div>
                <Select value={riskFilter} onValueChange={setRiskFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="countries-risk-filter"><SelectValue placeholder="Risk level" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All risk levels</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="elevated">Elevated</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={regionFilter} onValueChange={setRegionFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="countries-region-filter"><SelectValue placeholder="Region" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All regions</SelectItem>
                    <SelectItem value="apac">Asia-Pacific</SelectItem>
                    <SelectItem value="americas">Americas</SelectItem>
                    <SelectItem value="europe">Europe</SelectItem>
                    <SelectItem value="mea">Middle East & Africa</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]" data-testid="countries-sort"><SelectValue placeholder="Sort by" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="risk_desc">Highest risk first</SelectItem>
                    <SelectItem value="risk_asc">Lowest risk first</SelectItem>
                    <SelectItem value="alpha">Alphabetical</SelectItem>
                    <SelectItem value="movers">Biggest movers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Country Grid */}
          {loading ? (
            <div className="text-center py-20 text-[#4E6974]">Loading countries...</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {processed.map((c, i) => (
                <motion.div
                  key={c.country_code}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <Card
                    className="bg-white rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.04)] border-none hover:shadow-[0_8px_32px_rgba(0,0,0,0.1)] cursor-pointer transition-all hover:-translate-y-0.5"
                    onClick={() => navigate(`/countries/${c.country_code}`)}
                    data-testid={`country-card-${c.country_code.toLowerCase()}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{c.flag_emoji}</span>
                          <div>
                            <h3 className="text-sm font-bold text-[#0D3B4F]">{c.country_name}</h3>
                            <span className="text-[10px] text-[#4E6974] uppercase tracking-wide">{c.region === 'apac' ? 'Asia-Pacific' : c.region === 'americas' ? 'Americas' : c.region === 'europe' ? 'Europe' : c.region === 'mea' ? 'MEA' : c.country_code}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <TrendArrow trend={c.risk_trend} />
                        </div>
                      </div>

                      <div className="flex items-center gap-3 mb-3">
                        <div className="text-3xl font-bold text-[#0D3B4F]">{Math.round(c.overall_risk_score)}</div>
                        <Badge className={`${c.band.bg} ${c.band.text} text-xs`}>{c.band.level}</Badge>
                      </div>

                      {/* Risk bar */}
                      <div className="h-1.5 bg-[#F5F5F5] rounded-full overflow-hidden mb-3">
                        <div className="h-full rounded-full transition-all" style={{ width: `${c.overall_risk_score}%`, backgroundColor: c.band.color }} />
                      </div>

                      <p className="text-xs text-[#4E6974] leading-relaxed">{c.dominantTheme}</p>

                      <p className="text-[10px] text-[#4E6974]/70 mt-2 leading-relaxed">
                        {getRiskInterpretation(c.overall_risk_score, { entityName: c.country_name, entityType: 'country' }).substring(0, 150)}...
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
              {processed.length === 0 && (
                <div className="col-span-full text-center py-12 text-[#4E6974]">No countries match your filters</div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CountriesPage;
